import type { KeyboardEvent } from 'react';
import { useEffect, useState, useRef, forwardRef } from 'react';

import type { SpecificInputProps } from '../BaseInput';
import { Icon } from '@components/Icon/Icon';
import { Container, InnerContainer, InvisbleInput } from './styles';
import { Tag } from '@components/Tag/Tag';
import { Loader } from '@components/Loader/Loader';

export interface TagInputValue {
  /**
   * Specifies the tag name.
   */
  name: string;
  /**
   * Specifies if the tag is loading.
   */
  isLoading?: boolean;
}

export interface TagInputProps
  extends Omit<
    SpecificInputProps,
    | 'autoComplete'
    | 'actionContent'
    | 'customContent'
    | 'showInvalidIcon'
    | 'defaultValue'
    | 'onChange'
    | 'value'
    | 'required'
    | 'icon'
    | 'size'
    | 'isReadonly'
  > {
  /**
   * The tags value.
   */
  value?: TagInputValue[];
  /**
   * Callback when the tags have changed. This is only called when either `Enter`, `,` or a customTrigger is pressed.
   */
  onChange?: (value: TagInputValue[]) => void;
  /**
   * A key to trigger the onChange method. If the user presses this key, the onChange method will be called.
   */
  customTrigger?: KeyboardEvent['key'];
  /**
   * Size of the input. 2 sizes, to result in 40 or 56 px height if the tags are on 1 row. Size `m` corresponds with a Tag of size `s`, and size `m` with a `l` sized tag.
   */
  size?: 'm' | 'l';
}

/**
 * The Tag Input component allows you to type in multiple items as tags.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/423932-tag-input/b/648c83 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-textinput--basic-case Code documentation}
 *
 * @example
 * <TagInput value={[]} />
 */
export const TagInput = forwardRef<HTMLInputElement, TagInputProps>(
  (
    {
      value = [],
      onChange,
      className,
      inputStyles,
      isDisabled = false,
      isRequired = false,
      isInvalid = false,
      customTrigger,
      size = 'm',
      ...props
    }: TagInputProps,
    ref,
  ): JSX.Element => {
    const inputRef = useRef<HTMLInputElement>(null);
    const tagRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const [focusedTag, setFocusedTag] = useState<number>(-1);

    useEffect(() => {
      tagRefs.current[focusedTag]?.focus();
    }, [focusedTag]);

    const focusInput = () => {
      setFocusedTag(-1);
      inputRef.current.focus();
    };

    const focusNextTag = () => {
      const lastTagFocused = focusedTag === value.length - 1;
      if (lastTagFocused) {
        focusInput();
      } else {
        setFocusedTag(focusedTag + 1);
      }
    };

    const focusPreviousTag = () => {
      const firstTagFocused = focusedTag === 0;
      if (firstTagFocused) {
        focusInput();
      } else {
        const noTagFocused = focusedTag === -1;
        const lastTagIndex = value.length - 1;
        setFocusedTag(noTagFocused ? lastTagIndex : focusedTag - 1);
      }
    };

    const deleteTag = (tagIndex: number) => {
      const newValue = [...value];
      newValue.splice(tagIndex, 1);
      onChange(newValue);
      setFocusedTag(-1);
      focusInput();
    };

    const handleFocusEvents = (key: KeyboardEvent['key']) => {
      switch (key) {
        case 'ArrowLeft':
          focusPreviousTag();
          break;
        case 'ArrowRight':
          focusNextTag();
          break;
        case 'Backspace':
          focusedTag !== -1 ? deleteTag(focusedTag) : focusPreviousTag();
          break;
      }
    };

    const addTag = (event: KeyboardEvent<HTMLInputElement>) => {
      if (inputRef.current.value !== '') {
        event.preventDefault();
        onChange([...value, { name: inputRef.current.value }]);
        inputRef.current.value = '';
      }
    };

    const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
      const { key } = event;
      if (key === 'Enter' || key === ',' || key === customTrigger) {
        addTag(event);
      }

      if (inputRef.current.value === '') {
        handleFocusEvents(key);
      }
    };

    return (
      <Container
        ref={ref}
        onKeyDown={handleKeyPress}
        className={className}
        css={inputStyles}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        size={size}
      >
        <InnerContainer>
          {value?.length !== 0 &&
            !isDisabled &&
            value.map(({ name, isLoading }, i) => (
              <Tag
                key={`${name}-${i}`}
                focusRef={(el) => (tagRefs.current[i] = el)}
                size={size === 'm' ? 's' : 'm'}
                onDismiss={() => deleteTag(i)}
                isDismissible={!isLoading}
                prefixContent={isLoading ? <Loader /> : null}
              >
                {name}
              </Tag>
            ))}

          <InvisbleInput
            ref={inputRef}
            type="text"
            disabled={isDisabled}
            required={isRequired}
            tagSize={size}
            {...props}
          />
        </InnerContainer>

        {isInvalid && <Icon icon="error_alt" size="m" color="text_danger" />}
      </Container>
    );
  },
);

TagInput.displayName = 'TagInput';
